import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, message, Input, Select, Card, Tag, Image, DatePicker, InputNumber, Popconfirm, Upload, Radio, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined, UploadOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { eventService, eventCategoryService } from '../../services';
import RichTextEditor from '../../components/RichTextEditor';
import apiClient from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const formatMoney = (value) => new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0
}).format(Number(value) || 0);

const EventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ search: '', idphanloai: null, trangthai: null });
  const [form, setForm] = useState({
    tensukien: '',
    idphanloai: null,
    anhdaidien: '',
    vitri: '',
    noidung: '',
    date: null,
    gia: 0,
    chophepdutiec: 0,
    giadutiec: 0,
    mucdouutien: 1
  });
  const [contentHtml, setContentHtml] = useState('');
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [imageMethod, setImageMethod] = useState('url');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const richTextEditorRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getAll({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...filters
      });
      if (response && Array.isArray(response)) {
        setEvents(response);
        setPagination(prev => ({ ...prev, total: response[0]?.TotalRecords || 0 }));
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Lỗi khi tải danh sách sự kiện');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await eventCategoryService.getAll();
      if (response && Array.isArray(response)) setCategories(response);
    } catch (error) {
      console.error('Lỗi khi tải danh sách phân loại', error);
    }
  };

  const resetEventForm = () => {
    setForm({
      tensukien: '',
      idphanloai: null,
      anhdaidien: '',
      vitri: '',
      noidung: '',
      date: null,
      gia: 0,
      chophepdutiec: 0,
      giadutiec: 0,
      mucdouutien: 1
    });
  };

  const handleAdd = () => {
    setEditingEvent(null);
    resetEventForm();
    setContentHtml('');
    setShowContentPreview(false);
    setImageMethod('url');
    setUploadedImageUrl('');
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingEvent(record);
    setForm({
      tensukien: record.TenSuKien || '',
      idphanloai: record.IdPhanLoai || null,
      anhdaidien: record.AnhDaiDien || '',
      vitri: record.ViTri || '',
      noidung: record.NoiDung || '',
      date: record.Date ? dayjs(record.Date) : null,
      gia: Number(record.Gia) || 0,
      chophepdutiec: Number(record.ChoPhepDuTiec) === 1 ? 1 : 0,
      giadutiec: Number(record.GiaDuTiec) || 0,
      mucdouutien: record.MucDoUuTien || 1
    });
    setContentHtml(record.NoiDung || '');
    setShowContentPreview(false);
    setImageMethod('url');
    setUploadedImageUrl('');
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await eventService.delete(id);
      message.success('Xóa sự kiện thành công');
      fetchEvents();
    } catch (error) {
      message.error(error.response?.data?.error || 'Lỗi khi xóa sự kiện');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await eventService.updateStatus(id, newStatus);
      message.success('Cập nhật trạng thái thành công');
      fetchEvents();
    } catch (error) {
      message.error(error.response?.data?.error || 'Lỗi khi cập nhật trạng thái');
    }
  };

  const handleViewRegistrations = (event) => {
    navigate(`/events/${event.IdSuKien}/registrations`, { state: { event } });
  };

  const handleSubmit = async () => {
    if (!form.tensukien.trim()) {
      message.error('Vui lòng nhập tên sự kiện');
      return;
    }
    if (Number(form.gia) < 0 || Number(form.giadutiec) < 0) {
      message.error('Giá không được âm');
      return;
    }
    if (!form.chophepdutiec && Number(form.giadutiec) !== 0) {
      message.error('Sự kiện không cho dự tiệc thì giá dự tiệc phải bằng 0');
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const submitData = {
      IdSuKien: editingEvent ? editingEvent.IdSuKien : Date.now(),
      tensukien: form.tensukien,
      idphanloai: form.idphanloai,
      anhdaidien: form.anhdaidien || uploadedImageUrl || '',
      vitri: form.vitri || '',
      noidung: contentHtml || form.noidung || '',
      date: form.date ? form.date.format('YYYY-MM-DD') : null,
      gia: Number(form.gia) || 0,
      chophepdutiec: form.chophepdutiec ? 1 : 0,
      giadutiec: form.chophepdutiec ? (Number(form.giadutiec) || 0) : 0,
      mucdouutien: form.mucdouutien || 1
    };

    Object.keys(submitData).forEach(key => {
      if (submitData[key] === null || submitData[key] === undefined) delete submitData[key];
    });

    try {
      if (editingEvent) {
        await eventService.update(editingEvent.IdSuKien, submitData);
        message.success('Cập nhật sự kiện thành công');
      } else {
        await eventService.create(submitData);
        message.success('Thêm sự kiện thành công');
      }
      setModalVisible(false);
      fetchEvents();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Lỗi không xác định';
      message.error(editingEvent ? `Lỗi khi cập nhật: ${errorMessage}` : `Lỗi khi thêm mới: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({ ...pagination, current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const eventColumns = [
    { title: 'ID', dataIndex: 'IdSuKien', key: 'IdSuKien', width: 70 },
    {
      title: 'Ảnh', dataIndex: 'AnhDaiDien', key: 'AnhDaiDien', width: 90,
      render: (text) => text ? <Image width={60} height={40} src={text} style={{ objectFit: 'cover' }} /> : 'Không có'
    },
    { title: 'Tên Sự Kiện', dataIndex: 'TenSuKien', key: 'TenSuKien', width: 220 },
    { title: 'Phân Loại', dataIndex: 'TenPhanLoai', key: 'TenPhanLoai', width: 150 },
    { title: 'Vị Trí', dataIndex: 'ViTri', key: 'ViTri', width: 150 },
    { title: 'Ngày', dataIndex: 'Date', key: 'Date', width: 110, render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '' },
    { title: 'Giá sự kiện', dataIndex: 'Gia', key: 'Gia', width: 130, render: formatMoney },
    {
      title: 'Dự tiệc', key: 'Dinner', width: 150,
      render: (_, record) => Number(record.ChoPhepDuTiec) === 1
        ? <><Tag color="green">Có</Tag><div style={{ marginTop: 4 }}>{formatMoney(record.GiaDuTiec)}</div></>
        : <Tag>Không</Tag>
    },
    { title: 'Ưu Tiên', dataIndex: 'MucDoUuTien', key: 'MucDoUuTien', width: 80 },
    {
      title: 'Trạng Thái', dataIndex: 'TrangThai', key: 'TrangThai', width: 150,
      render: (status, record) => (
        <Select value={status} style={{ width: 135 }} onChange={(value) => handleStatusChange(record.IdSuKien, value)}>
          <Option value="Đang hoạt động">Đang hoạt động</Option>
          <Option value="Đã Xong">Đã Xong</Option>
          <Option value="Hủy">Hủy</Option>
        </Select>
      )
    },
    {
      title: 'Thao tác', key: 'action', width: 270, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" onClick={() => handleViewRegistrations(record)}>Danh sách đăng ký</Button>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Bạn có chắc chắn muốn xóa?" onConfirm={() => handleDelete(record.IdSuKien)} okText="Có" cancelText="Không">
            <Button type="primary" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="Quản lý Sự Kiện">
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Input
            placeholder="Tìm kiếm theo tên sự kiện..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 250 }}
          />
          <Select
            placeholder="Chọn phân loại"
            value={filters.idphanloai}
            onChange={(value) => setFilters({ ...filters, idphanloai: value })}
            style={{ width: 200 }}
            allowClear
          >
            {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.TenSuKien}</Option>)}
          </Select>
          <Select
            placeholder="Chọn trạng thái"
            value={filters.trangthai}
            onChange={(value) => setFilters({ ...filters, trangthai: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="Đang hoạt động">Đang hoạt động</Option>
            <Option value="Đã Xong">Đã Xong</Option>
            <Option value="Hủy">Hủy</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Thêm Sự Kiện</Button>
        </div>

        <Table
          columns={eventColumns}
          dataSource={events}
          rowKey="IdSuKien"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Tổng ${total} sự kiện`
          }}
          onChange={handleTableChange}
        />

        <Modal
          title={editingEvent ? 'Sửa Sự Kiện' : 'Thêm Sự Kiện'}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={() => setModalVisible(false)}
          okText={editingEvent ? 'Cập nhật' : 'Thêm'}
          cancelText="Hủy"
          confirmLoading={submitting}
          width={700}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label>Tên Sự Kiện *</label>
              <Input value={form.tensukien} onChange={(e) => setForm({ ...form, tensukien: e.target.value })} placeholder="Nhập tên sự kiện" />
            </div>
            <div>
              <label>Phân Loại</label>
              <Select value={form.idphanloai} onChange={(value) => setForm({ ...form, idphanloai: value })} placeholder="Chọn phân loại" style={{ width: '100%' }}>
                {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.TenSuKien}</Option>)}
              </Select>
            </div>
            <div>
              <label>Ảnh Đại Diện</label>
              <Radio.Group
                value={imageMethod}
                onChange={(e) => {
                  setImageMethod(e.target.value);
                  if (e.target.value === 'url') setUploadedImageUrl('');
                  else setForm(prev => ({ ...prev, anhdaidien: '' }));
                }}
                style={{ marginBottom: 16 }}
              >
                <Radio.Button value="url"><LinkOutlined /> URL</Radio.Button>
                <Radio.Button value="upload"><UploadOutlined /> Upload</Radio.Button>
              </Radio.Group>
              {imageMethod === 'url' ? (
                <Input value={form.anhdaidien} onChange={(e) => setForm({ ...form, anhdaidien: e.target.value })} placeholder="https://example.com/image.jpg" prefix={<LinkOutlined />} />
              ) : (
                <Upload
                  name="image"
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    setUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('image', file);
                      const response = await apiClient.post('/api/uploads/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      if (response.data.success) {
                        const imageUrl = response.data.data.url;
                        setUploadedImageUrl(imageUrl);
                        setForm(prev => ({ ...prev, anhdaidien: imageUrl }));
                        message.success('Upload ảnh thành công!');
                      } else {
                        message.error('Upload ảnh thất bại!');
                      }
                    } catch (error) {
                      message.error('Không thể upload ảnh. Vui lòng thử lại!');
                    } finally {
                      setUploading(false);
                    }
                    return false;
                  }}
                  disabled={uploading}
                >
                  {uploadedImageUrl
                    ? <Image src={uploadedImageUrl} alt="Uploaded" style={{ width: '100%', height: '100%', objectFit: 'cover' }} preview />
                    : <div>{uploading ? <Spin /> : <><PictureOutlined style={{ fontSize: 24, color: '#999' }} /><div style={{ marginTop: 8, color: '#999' }}>Click để upload</div></>}</div>}
                </Upload>
              )}
            </div>
            <div>
              <label>Vị Trí</label>
              <Input value={form.vitri} onChange={(e) => setForm({ ...form, vitri: e.target.value })} placeholder="Nhập vị trí tổ chức" />
            </div>
            <div>
              <label>
                <Space>
                  <span>Nội Dung</span>
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setShowContentPreview(!showContentPreview)}>
                    {showContentPreview ? 'Chỉnh sửa' : 'Xem trước'}
                  </Button>
                </Space>
              </label>
              {showContentPreview
                ? <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 16, minHeight: 200, background: '#fff', fontSize: 14, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: contentHtml }} />
                : <RichTextEditor ref={richTextEditorRef} value={contentHtml} onChange={(content) => { setContentHtml(content); setForm(prev => ({ ...prev, noidung: content })); }} placeholder="Nhập nội dung chi tiết sự kiện..." />}
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label>Ngày Tổ Chức</label>
                <DatePicker value={form.date} onChange={(date) => setForm({ ...form, date })} style={{ width: '100%' }} format="DD/MM/YYYY" />
              </div>
              <div style={{ flex: 1 }}>
                <label>Giá sự kiện / người đi cùng (VND)</label>
                <InputNumber value={form.gia} onChange={(value) => setForm({ ...form, gia: value || 0 })} style={{ width: '100%' }} min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
              </div>
            </div>
            <div style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 8, background: '#fafafa' }}>
              <div style={{ marginBottom: 12 }}><strong>Dự tiệc</strong></div>
              <Radio.Group
                value={form.chophepdutiec}
                onChange={(e) => setForm(prev => ({ ...prev, chophepdutiec: e.target.value, giadutiec: e.target.value ? prev.giadutiec : 0 }))}
              >
                <Radio value={0}>Không có dự tiệc</Radio>
                <Radio value={1}>Có dự tiệc</Radio>
              </Radio.Group>
              {form.chophepdutiec === 1 && (
                <div style={{ marginTop: 12 }}>
                  <label>Giá dự tiệc / người (VND)</label>
                  <InputNumber value={form.giadutiec} onChange={(value) => setForm({ ...form, giadutiec: value || 0 })} style={{ width: '100%' }} min={0} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
                </div>
              )}
            </div>
            <div>
              <label>Mức Độ Ưu Tiên</label>
              <InputNumber value={form.mucdouutien} onChange={(value) => setForm({ ...form, mucdouutien: value || 1 })} style={{ width: '100%' }} min={1} max={999} addonAfter="Số càng lớn càng ưu tiên" />
            </div>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default EventList;
